/**
 * 版本对比弹窗 - 逐章节对比两个版本的内容差异
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { VersionDetail, versionApi } from '../services/api';
import MarkdownRenderer from './MarkdownRenderer';

interface DiffViewerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  /** 版本 A（基准版本，在左侧/红色删除） */
  versionA: VersionDetail;
  /** 版本 A 的 ID（用于保存编辑内容） */
  versionAId: number;
  /** 版本 A 名称 */
  versionAName: string;
  /** 版本 B（对比版本，在右侧/绿色新增） */
  versionB: VersionDetail;
  /** 版本 B 的 ID（用于保存编辑内容）；id=0 表示未保存的当前版本 */
  versionBId: number;
  /** 版本 B 名称 */
  versionBName: string;
  /** 当版本 B 是未保存版本时，编辑保存的回调 */
  onUpdateCurrentContent?: (nodeId: string, content: string) => Promise<void>;
}

/** 单章节的差异结果 */
interface ChapterDiff {
  nodeId: string;
  title: string;
  status: 'added' | 'removed' | 'modified' | 'unchanged';
  /** 版本A（基准）内容 */
  contentA: string;
  /** 版本B（对比）内容 */
  contentB: string;
  /** 单章相似度（0-100） */
  similarity: number;
  /** 行级差异（仅在 modified 状态下有值） */
  lineDiffs?: LineDiff[];
}

interface LineDiff {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
}

/** 章节列表树的节点结构 */
interface ChapterTreeNode {
  nodeId: string;
  title: string;
  children: ChapterTreeNode[];
  diff: ChapterDiff | null;
}

/** 简单的行级 diff 算法：oldText=版本A, newText=版本B */
const computeLineDiff = (oldText: string, newText: string): LineDiff[] => {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const result: LineDiff[] = [];

  // 使用 LCS 做行级对比
  const m = oldLines.length;
  const n = newLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // 回溯构建 diff
  const backtrack = (i: number, j: number): LineDiff[] => {
    if (i === 0 && j === 0) return [];
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      return [...backtrack(i - 1, j - 1), { type: 'unchanged', text: oldLines[i - 1] }];
    }
    if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      return [...backtrack(i, j - 1), { type: 'added', text: newLines[j - 1] }];
    }
    return [...backtrack(i - 1, j), { type: 'removed', text: oldLines[i - 1] }];
  };

  return backtrack(m, n);
};

/** 简单的文本相似度（用于统计） */
const computeSimilarity = (a: string, b: string): number => {
  if (!a && !b) return 100;
  if (!a || !b) return 0;
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  if (longer.length === 0) return 100;
  // Jaccard-like: 相同行数 / 总行数
  const aLines = new Set(a.split('\n').map(l => l.trim()).filter(Boolean));
  const bLines = new Set(b.split('\n').map(l => l.trim()).filter(Boolean));
  let intersect = 0;
  aLines.forEach(line => { if (bLines.has(line)) intersect++; });
  const union = aLines.size + bLines.size - intersect;
  return union === 0 ? 100 : Math.round((intersect / union) * 100);
};

/** 从两个版本数据计算所有章节的对比结果 */
const computeChapterDiffs = (versionA: VersionDetail, versionB: VersionDetail): ChapterDiff[] => {
  const diffs: ChapterDiff[] = [];
  const nodesA = versionA.outline_nodes || [];
  const contentsA = versionA.chapter_contents || {};
  const nodesB = versionB.outline_nodes || [];
  const contentsB = versionB.chapter_contents || {};

  const nodeTitleMap: Record<string, string> = {};
  nodesA.forEach((n: any) => { nodeTitleMap[n.node_id] = n.title || n.node_id; });
  nodesB.forEach((n: any) => { nodeTitleMap[n.node_id] = n.title || n.node_id; });

  const allNodeIds = new Set<string>();
  Object.keys(contentsA).forEach(id => allNodeIds.add(id));
  Object.keys(contentsB).forEach(id => allNodeIds.add(id));
  Object.keys(nodeTitleMap).forEach(id => allNodeIds.add(id));

  allNodeIds.forEach(nodeId => {
    const contentA = contentsA[nodeId] || '';
    const contentB = contentsB[nodeId] || '';
    const title = nodeTitleMap[nodeId] || nodeId;

    let status: ChapterDiff['status'];
    if (contentB && !contentA) status = 'added';
    else if (!contentB && contentA) status = 'removed';
    else if (contentA !== contentB) status = 'modified';
    else status = 'unchanged';

    const similarity = computeSimilarity(contentA, contentB);
    const diff: ChapterDiff = {
      nodeId, title, status, contentA, contentB, similarity,
    };
    if (status === 'modified') {
      diff.lineDiffs = computeLineDiff(contentA, contentB);
    }
    diffs.push(diff);
  });

  const statusOrder: Record<string, number> = { modified: 0, added: 1, removed: 2, unchanged: 3 };
  diffs.sort((a, b) => {
    const so = statusOrder[a.status] - statusOrder[b.status];
    if (so !== 0) return so;
    return a.nodeId.localeCompare(b.nodeId);
  });

  return diffs;
};

const DiffViewer: React.FC<DiffViewerProps> = ({
  isOpen,
  onClose,
  projectId,
  versionA,
  versionAId,
  versionAName,
  versionB,
  versionBId,
  versionBName,
  onUpdateCurrentContent,
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'side-by-side' | 'unified'>('side-by-side');
  // 并排模式下左右两侧的显示模式：text=原始文本, preview=渲染预览
  const [leftDisplayMode, setLeftDisplayMode] = useState<'text' | 'preview'>('text');
  const [rightDisplayMode, setRightDisplayMode] = useState<'text' | 'preview'>('text');
  // 树形章节列表的折叠状态（默认全部展开，即空集合）
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set());

  // ===== 编辑状态 =====
  const [editingLeft, setEditingLeft] = useState(false);
  const [editingRight, setEditingRight] = useState(false);
  const [editContentLeft, setEditContentLeft] = useState('');
  const [editContentRight, setEditContentRight] = useState('');
  const [savingLeft, setSavingLeft] = useState(false);
  const [savingRight, setSavingRight] = useState(false);

  // 构建所有章节的对比数据（使用 state 以便编辑后更新）
  const [chapterDiffs, setChapterDiffs] = useState<ChapterDiff[]>([]);
  useEffect(() => {
    setChapterDiffs(computeChapterDiffs(versionA, versionB));
  }, [versionA, versionB]);

  // 切换章节时取消编辑状态
  useEffect(() => {
    setEditingLeft(false);
    setEditingRight(false);
    setEditContentLeft('');
    setEditContentRight('');
  }, [selectedNodeId]);

  // 从 outline_nodes 构建树形结构，用于左侧章节列表展示
  const chapterTree = useMemo<ChapterTreeNode[]>(() => {
    // 合并两个版本的 outline_nodes，版本B 优先（作为对比目标，结构以B为准）
    const nodeDataMap = new Map<string, any>();
    const bNodeIds = new Set<string>();

    (versionB.outline_nodes || []).forEach((n: any) => {
      nodeDataMap.set(n.node_id, n);
      bNodeIds.add(n.node_id);
    });
    (versionA.outline_nodes || []).forEach((n: any) => {
      if (!bNodeIds.has(n.node_id)) {
        nodeDataMap.set(n.node_id, n);
      }
    });

    // 第一遍：创建所有树节点
    const treeNodes = new Map<string, ChapterTreeNode>();
    Array.from(nodeDataMap.values()).forEach((n: any) => {
      treeNodes.set(n.node_id, {
        nodeId: n.node_id,
        title: n.title || n.node_id,
        children: [],
        diff: null,
      });
    });

    // 关联 diff 信息
    chapterDiffs.forEach(d => {
      const node = treeNodes.get(d.nodeId);
      if (node) node.diff = d;
    });

    // 第二遍：构建父子关系（以B的结构为准）
    const roots: ChapterTreeNode[] = [];
    treeNodes.forEach((treeNode, nodeId) => {
      const nodeData = nodeDataMap.get(nodeId);
      const parentId = nodeData?.parent_node_id || null;
      if (parentId && treeNodes.has(parentId)) {
        treeNodes.get(parentId)!.children.push(treeNode);
      } else {
        roots.push(treeNode);
      }
    });

    // 递归排序子节点（按 node_id 自然排序）
    const sortChildren = (nodes: ChapterTreeNode[]) => {
      nodes.sort((a, b) => a.nodeId.localeCompare(b.nodeId, undefined, { numeric: true }));
      nodes.forEach(n => sortChildren(n.children));
    };
    sortChildren(roots);

    return roots;
  }, [chapterDiffs, versionA.outline_nodes, versionB.outline_nodes]);

  // 统计
  const stats = useMemo(() => {
    const modified = chapterDiffs.filter(d => d.status === 'modified').length;
    const added = chapterDiffs.filter(d => d.status === 'added').length;
    const removed = chapterDiffs.filter(d => d.status === 'removed').length;
    const unchanged = chapterDiffs.filter(d => d.status === 'unchanged').length;

    let totalSimilarity = 0;
    let compareCount = 0;
    chapterDiffs.forEach(d => {
      if (d.status !== 'added' && d.status !== 'removed') {
        totalSimilarity += d.similarity;
        compareCount++;
      }
    });
    const avgSimilarity = compareCount > 0 ? Math.round(totalSimilarity / compareCount) : 100;

    // 章节描述相似度：比较两个版本中相同 node_id 的描述文本
    const nodesA = versionA.outline_nodes || [];
    const nodesB = versionB.outline_nodes || [];
    const descMapA: Record<string, string> = {};
    nodesA.forEach((n: any) => { descMapA[n.node_id] = n.description || ''; });
    const descMapB: Record<string, string> = {};
    nodesB.forEach((n: any) => { descMapB[n.node_id] = n.description || ''; });

    let descTotalSimilarity = 0;
    let descCount = 0;
    Object.keys(descMapA).forEach(nodeId => {
      if (nodeId in descMapB) {
        descTotalSimilarity += computeSimilarity(descMapA[nodeId], descMapB[nodeId]);
        descCount++;
      }
    });
    // 仅 B 独有的节点也计入（按空缺=0相似）
    Object.keys(descMapB).forEach(nodeId => {
      if (!(nodeId in descMapA)) descCount++;
    });
    const descSimilarity = descCount > 0 ? Math.round(descTotalSimilarity / descCount) : 100;

    return { modified, added, removed, unchanged, avgSimilarity, descSimilarity, total: chapterDiffs.length };
  }, [chapterDiffs, versionA.outline_nodes, versionB.outline_nodes]);

  // 默认选中第一个有差异的章节 / 切换章节时重置显示模式为文本
  useEffect(() => {
    if (isOpen && chapterDiffs.length > 0) {
      const firstDiff = chapterDiffs.find(d => d.status !== 'unchanged');
      setSelectedNodeId(firstDiff ? firstDiff.nodeId : chapterDiffs[0].nodeId);
    }
    // 切换章节时重置左右面板为文本模式
    setLeftDisplayMode('text');
    setRightDisplayMode('text');
  }, [isOpen, chapterDiffs]);

  // 点击章节列表切换章节时也重置显示模式
  useEffect(() => {
    setLeftDisplayMode('text');
    setRightDisplayMode('text');
  }, [selectedNodeId]);

  // ESC 关闭
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const selectedDiff = chapterDiffs.find(d => d.nodeId === selectedNodeId);

  // 导航到上一个/下一个有差异的章节
  const navigateDiff = useCallback((direction: 'prev' | 'next') => {
    const diffIds = chapterDiffs.filter(d => d.status !== 'unchanged').map(d => d.nodeId);
    if (diffIds.length === 0) return;
    const currentIdx = diffIds.indexOf(selectedNodeId || '');
    if (direction === 'prev') {
      const prevIdx = currentIdx <= 0 ? diffIds.length - 1 : currentIdx - 1;
      setSelectedNodeId(diffIds[prevIdx]);
    } else {
      const nextIdx = currentIdx >= diffIds.length - 1 ? 0 : currentIdx + 1;
      setSelectedNodeId(diffIds[nextIdx]);
    }
  }, [chapterDiffs, selectedNodeId]);

  // ===== 编辑/保存处理函数 =====

  /** 进入左侧编辑模式 */
  const startEditLeft = (currentContent: string) => {
    setEditContentLeft(currentContent);
    setEditingLeft(true);
    setEditingRight(false);
  };

  /** 进入右侧编辑模式 */
  const startEditRight = (currentContent: string) => {
    setEditContentRight(currentContent);
    setEditingRight(true);
    setEditingLeft(false);
  };

  /** 取消编辑 */
  const cancelEdit = () => {
    setEditingLeft(false);
    setEditingRight(false);
    setEditContentLeft('');
    setEditContentRight('');
  };

  /** 保存左侧编辑内容 */
  const handleSaveLeft = async (nodeId: string) => {
    setSavingLeft(true);
    try {
      await versionApi.updateVersionChapterContent(projectId, versionAId, nodeId, editContentLeft);
      // 本地更新 diffs 状态
      setChapterDiffs(prev =>
        prev.map(d => {
          if (d.nodeId === nodeId) {
            const newContentA = editContentLeft;
            const contentB = d.contentB;
            let status: ChapterDiff['status'];
            if (newContentA === contentB) status = 'unchanged';
            else if (!newContentA && contentB) status = 'added';
            else if (newContentA && !contentB) status = 'removed';
            else status = 'modified';
            return {
              ...d,
              contentA: newContentA,
              status,
              similarity: computeSimilarity(newContentA, contentB),
              lineDiffs: status === 'modified' ? computeLineDiff(newContentA, contentB) : undefined,
            };
          }
          return d;
        })
      );
      setEditingLeft(false);
    } catch {
      alert('保存失败，请重试');
    } finally {
      setSavingLeft(false);
    }
  };

  /** 保存右侧编辑内容 */
  const handleSaveRight = async (nodeId: string) => {
    setSavingRight(true);
    try {
      if (versionBId === 0 && onUpdateCurrentContent) {
        // 保存到当前未保存版本（回写到编辑器）
        await onUpdateCurrentContent(nodeId, editContentRight);
      } else {
        // 保存到后端已保存版本
        await versionApi.updateVersionChapterContent(projectId, versionBId, nodeId, editContentRight);
      }
      setChapterDiffs(prev =>
        prev.map(d => {
          if (d.nodeId === nodeId) {
            const contentA = d.contentA;
            const newContentB = editContentRight;
            let status: ChapterDiff['status'];
            if (contentA === newContentB) status = 'unchanged';
            else if (!contentA && newContentB) status = 'added';
            else if (contentA && !newContentB) status = 'removed';
            else status = 'modified';
            return {
              ...d,
              contentB: newContentB,
              status,
              similarity: computeSimilarity(contentA, newContentB),
              lineDiffs: status === 'modified' ? computeLineDiff(contentA, newContentB) : undefined,
            };
          }
          return d;
        })
      );
      setEditingRight(false);
    } catch {
      alert('保存失败，请重试');
    } finally {
      setSavingRight(false);
    }
  };

  const statusBadge = (status: ChapterDiff['status']) => {
    switch (status) {
      case 'modified':
        return <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">已修改</span>;
      case 'added':
        return <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">新增</span>;
      case 'removed':
        return <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">已删除</span>;
      case 'unchanged':
        return <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">未变更</span>;
    }
  };

  // 递归渲染树形章节节点
  const renderChapterTreeNode = useCallback(
    (node: ChapterTreeNode, level: number = 0): React.ReactNode => {
      const hasChildren = node.children.length > 0;
      const isCollapsed = collapsedNodes.has(node.nodeId);
      const diff = node.diff;

      const toggleCollapse = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCollapsedNodes(prev => {
          const next = new Set(prev);
          if (isCollapsed) next.delete(node.nodeId);
          else next.add(node.nodeId);
          return next;
        });
      };

      const selectNode = () => {
        // 有 diff 信息的节点（即有内容的章节）才可选中
        if (diff) {
          setSelectedNodeId(node.nodeId);
        }
      };

      return (
        <div key={node.nodeId}>
          <button
            onClick={selectNode}
            className={`w-full text-left px-3 py-1.5 border-b border-gray-50 transition-colors ${
              selectedNodeId === node.nodeId
                ? 'bg-blue-50'
                : 'hover:bg-gray-50'
            }`}
            style={{ paddingLeft: `${12 + level * 16}px` }}
          >
            <div className="flex items-center gap-1">
              {hasChildren ? (
                <button
                  onClick={toggleCollapse}
                  className="p-0.5 rounded hover:bg-gray-200 flex-shrink-0"
                  title={isCollapsed ? '展开' : '折叠'}
                >
                  {isCollapsed ? (
                    <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </button>
              ) : (
                <DocumentTextIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0 flex items-center gap-1.5">
                {selectedNodeId === node.nodeId && (
                  <span className="w-0.5 h-3.5 bg-blue-500 rounded-full flex-shrink-0" />
                )}
                <span className="text-[10px] text-gray-400 flex-shrink-0">{node.nodeId}</span>
                <span className="text-xs text-gray-700 truncate">{node.title}</span>
              </div>
            </div>
            {diff && (
              <div className="mt-0.5 flex items-center gap-1.5" style={{ marginLeft: hasChildren ? 20 : 16 }}>
                {statusBadge(diff.status)}
                {diff.status !== 'added' && diff.status !== 'removed' && (
                  <span className="text-[10px] text-gray-400">{diff.similarity}%</span>
                )}
              </div>
            )}
          </button>
          {hasChildren && !isCollapsed && (
            <div>
              {node.children.map(child => renderChapterTreeNode(child, level + 1))}
            </div>
          )}
        </div>
      );
    },
    [collapsedNodes, selectedNodeId, statusBadge],
  );

  if (!isOpen) return null;

  const renderLineDiff = (lineDiffs: LineDiff[]) => {
    return lineDiffs.map((line, i) => (
      <div
        key={i}
        className={`text-xs font-mono px-2 py-0.5 leading-relaxed ${
          line.type === 'added'
            ? 'bg-green-50 text-green-800'
            : line.type === 'removed'
            ? 'bg-red-50 text-red-800'
            : 'text-gray-600'
        }`}
      >
        <span className="inline-block w-5 mr-1 text-gray-400 select-none">
          {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
        </span>
        {line.text || ' '}
      </div>
    ));
  };

  const renderUnifiedDiff = (diff: ChapterDiff) => {
    if (!diff.lineDiffs) {
      return (
        <div className="p-4 text-sm text-gray-500 text-center">内容相同，无差异</div>
      );
    }
    return (
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200">
          <span className="text-xs text-gray-500">
            统一视图 — 红色为版本A（{versionAName}）删除内容，绿色为版本B（{versionBName}）新增内容
          </span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {renderLineDiff(diff.lineDiffs)}
        </div>
      </div>
    );
  };

  const renderSideBySide = (diff: ChapterDiff) => {
    // 左右面板切换按钮的选中态颜色
    const activeColors: Record<string, string> = {
      blue: 'bg-blue-500 text-white',
      purple: 'bg-purple-500 text-white',
    };

    // 获取当前章节在 A / B 中的描述
    const nodeDescA = (versionA.outline_nodes || []).find((n: any) => n.node_id === diff.nodeId)?.description || '';
    const nodeDescB = (versionB.outline_nodes || []).find((n: any) => n.node_id === diff.nodeId)?.description || '';

    const displayModeBar = (
      label: string,
      mode: 'text' | 'preview',
      setMode: (m: 'text' | 'preview') => void,
      accentColor: string,
    ) => (
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500 mr-auto truncate">{label}</span>
        <div className="flex items-center bg-gray-100 rounded-md overflow-hidden flex-shrink-0">
          <button
            onClick={() => setMode('text')}
            className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
              mode === 'text' ? activeColors[accentColor] : 'text-gray-500 hover:bg-gray-200'
            }`}
          >
            文本
          </button>
          <button
            onClick={() => setMode('preview')}
            className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
              mode === 'preview' ? activeColors[accentColor] : 'text-gray-500 hover:bg-gray-200'
            }`}
          >
            预览
          </button>
        </div>
      </div>
    );

    const renderDescription = (desc: string, emptyLabel: string) => {
      if (!desc) {
        return <div className="px-3 py-2 text-xs text-gray-400 italic">{emptyLabel}</div>;
      }
      return <div className="px-3 py-2 text-xs text-gray-600 bg-gray-50/50 border-b border-gray-100">{desc}</div>;
    };

    const renderContent = (content: string, mode: 'text' | 'preview', emptyLabel: string) => {
      if (!content) {
        return <div className="p-4 text-xs text-gray-400 text-center">{emptyLabel}</div>;
      }
      if (mode === 'preview') {
        return (
          <div className="p-3">
            <MarkdownRenderer
              content={content}
              enableMermaid={true}
              enableMath={true}
              mermaidDefaultView="code"
            />
          </div>
        );
      }
      return (
        <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono p-3 m-0">{content}</pre>
      );
    };

    // 编辑/保存按钮栏
    const editToolbar = (
      isEditing: boolean,
      isSaving: boolean,
      onEdit: () => void,
      onSave: () => void,
      onCancel: () => void,
    ): React.ReactNode => {
      if (isEditing) {
        return (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onSave}
              disabled={isSaving}
              className="flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 transition-colors"
            >
              <CheckIcon className="w-3 h-3" />
              {isSaving ? '保存中...' : '保存'}
            </button>
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-200 disabled:opacity-50"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      }
      return (
        <button
          onClick={onEdit}
          className="flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex-shrink-0"
          title="编辑此章节内容"
        >
          <PencilSquareIcon className="w-3 h-3" />
          编辑
        </button>
      );
    };

    return (
      <div className="grid grid-cols-2 gap-3">
        {/* 版本 A（基准） */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="bg-gray-50 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
            {displayModeBar(`版本A（基准）：${versionAName}`, leftDisplayMode, setLeftDisplayMode, 'blue')}
            {editToolbar(
              editingLeft,
              savingLeft,
              () => { startEditLeft(diff.contentA); setLeftDisplayMode('text'); },
              () => handleSaveLeft(diff.nodeId),
              cancelEdit,
            )}
          </div>
          <div className="px-3 py-1.5 border-b border-gray-100">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">章节描述</span>
            {renderDescription(nodeDescA, '（无描述）')}
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            {editingLeft ? (
              <textarea
                value={editContentLeft}
                onChange={(e) => setEditContentLeft(e.target.value)}
                className="w-full min-h-[50vh] p-3 text-sm font-mono text-gray-700 border-0 focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                placeholder="输入内容..."
                autoFocus
              />
            ) : (
              renderContent(diff.contentA, leftDisplayMode, '（无内容）')
            )}
          </div>
        </div>
        {/* 版本 B（对比） */}
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="bg-blue-50 px-3 py-1.5 border-b border-gray-200 flex items-center justify-between">
            {displayModeBar(`版本B（对比）：${versionBName}`, rightDisplayMode, setRightDisplayMode, 'purple')}
            {versionBId !== 0 ? (
              editToolbar(
                editingRight,
                savingRight,
                () => { startEditRight(diff.contentB); setRightDisplayMode('text'); },
                () => handleSaveRight(diff.nodeId),
                cancelEdit,
              )
            ) : (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[10px] text-gray-400 italic">未保存版本</span>
                {editToolbar(
                  editingRight,
                  savingRight,
                  () => { startEditRight(diff.contentB); setRightDisplayMode('text'); },
                  () => handleSaveRight(diff.nodeId),
                  cancelEdit,
                )}
              </div>
            )}
          </div>
          <div className="px-3 py-1.5 border-b border-gray-100">
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">章节描述</span>
            {renderDescription(nodeDescB, '（无描述）')}
          </div>
          <div className="max-h-[50vh] overflow-y-auto">
            {editingRight ? (
              <textarea
                value={editContentRight}
                onChange={(e) => setEditContentRight(e.target.value)}
                className="w-full min-h-[50vh] p-3 text-sm font-mono text-gray-700 border-0 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-y"
                placeholder="输入内容..."
                autoFocus
              />
            ) : (
              renderContent(diff.contentB, rightDisplayMode, '（无内容）')
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center pt-[5vh]">
      {/* 遮罩 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* 弹窗 */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-7xl mx-4 max-h-[90vh] flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">版本对比</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              <span className="font-medium text-gray-600">{versionAName}</span>
              <span className="mx-2 text-gray-400">vs</span>
              <span className="font-medium text-purple-600">{versionBName}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* 差异导航 */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => navigateDiff('prev')}
                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="上一个差异"
              >
                <ArrowLeftIcon className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500 min-w-[60px] text-center">
                {chapterDiffs.filter(d => d.status !== 'unchanged').indexOf(chapterDiffs.find(d => d.nodeId === selectedNodeId)!) + 1}
                {' / '}
                {chapterDiffs.filter(d => d.status !== 'unchanged').length}
              </span>
              <button
                onClick={() => navigateDiff('next')}
                className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="下一个差异"
              >
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
            {/* 视图切换 */}
            <div className="flex items-center bg-gray-100 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('side-by-side')}
                className={`px-3 py-1 text-xs transition-colors ${
                  viewMode === 'side-by-side' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                并排
              </button>
              <button
                onClick={() => setViewMode('unified')}
                className={`px-3 py-1 text-xs transition-colors ${
                  viewMode === 'unified' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                统一
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 统计栏 */}
        <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0 flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="text-gray-600">已修改 <strong>{stats.modified}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
            <span className="text-gray-600">新增 <strong>{stats.added}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="text-gray-600">已删除 <strong>{stats.removed}</strong></span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            <span className="text-gray-600">未变更 <strong>{stats.unchanged}</strong></span>
          </div>
          <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
            <span>
              章节描述相似度：<strong className="text-gray-700">{stats.descSimilarity}%</strong>
            </span>
            <span className="text-gray-300">|</span>
            <span>
              内容整体相似度：<strong className="text-gray-700">{stats.avgSimilarity}%</strong>
            </span>
          </div>
        </div>

        {/* 主内容区 */}
        <div className="flex-1 flex overflow-hidden">
          {/* 左侧章节列表 - 树形结构 */}
          <div className="w-64 border-r border-gray-200 overflow-y-auto flex-shrink-0">
            <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100 flex items-center justify-between">
              <span>章节列表（{stats.total}）</span>
              <span className="text-[10px] text-gray-400">目录结构</span>
            </div>
            {chapterTree.map(node => renderChapterTreeNode(node))}
          </div>

          {/* 右侧差异详情 */}
          <div className="flex-1 overflow-y-auto p-4">
            {selectedDiff ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {selectedDiff.nodeId} {selectedDiff.title}
                  </h3>
                  {statusBadge(selectedDiff.status)}
                </div>
                {viewMode === 'side-by-side'
                  ? renderSideBySide(selectedDiff)
                  : renderUnifiedDiff(selectedDiff)
                }
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                请从左侧选择章节查看差异
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiffViewer;
